import entries from "lodash/entries";
import { withDefault, definedMap, hasOwnProperty } from "collection-utils";

import {
    TitleDescription,
    isPrimitiveType,
    PrimitiveGlideTypeKind,
    ImageDescription,
    ImageKind,
    TableGlideType,
    getTableColumn,
    ClassScreenDescription,
    ArrayFilter,
    InputOutputTables,
    TableRefGlideType,
    compoundTypeScreenName,
    ColumnType,
    ArrayTransform,
    ArrayTransformKind,
    ColumnAssignment,
    EditScreenDescription,
    ComponentDescription,
    BinaryPredicateFormulaOperator,
    FormulaKind,
    isStringTypeKind,
    getTableName,
    isEmailArrayFilter,
    MutatingScreenKind,
    TableColumn,
    ActionDescription,
    webViewScreenName,
    SourceColumn,
    SourceColumnKind,
    LegacyPropertyDescription,
    ColumnTypeKind,
    DefaultableComponentDescription,
    isDateTimeTypeKind,
    SpecialValueKind,
    PropertyDescription,
    isNumberTypeKind,
    specialValueTypeKinds,
    isPrimitiveTypeKind,
    isComputedColumn,
    TableAndColumn,
} from "./description";
import { SerializedComponent, SerializedMenuItem, ScreenFlag } from "../components/SerializedApp";
import { SerializedElementBuilder, LetBinding, RowIndexOrRowID } from "./serialized-component";
import {
    Summary,
    ComponentConfiguratorContext,
    ComponentBuilder,
    FetchingBuilder,
    ComponentHandler,
    ValueForColumn,
} from "./components";
import {
    assertNever,
    isArray,
    panic,
    mapFilterUndefined,
    assert,
    parseUserDateTimeZoneAgnosticSync,
    dontAwait,
    parseUserDateTimeZoneAgnostic,
    undefinedIfEmptyString,
    isUrl,
    defined,
} from "../Support";
import {
    getEnumProperty,
    getColumnProperty,
    getStringProperty,
    getSourceColumnProperty,
    getNumberProperty,
    getSpecialValueProperty,
} from "./components/property-utils";
import { logError } from "../debug-print";
import { getColumnAssignments, inputOutputTablesForClassOrArrayScreen } from "./description-utils";
import { handlerForComponentKind } from "./handlers";
import { compileFormula, getPropertyWithColumnFormat, compileFilter, withColumnFormat } from "./formulas";
import { QuotaBannerRequirements, QuotaBannerMessage } from "../components/types";
import { handlerForPropertyKind } from "./components/description-handlers";
import { rowIndexColumnName, isFavoritedColumnName } from "../google-sheets";
import { handlerForActionKind } from "./actions";
import { Icon } from "../components/icons";
import { LocalizedStringKey } from "../components/localization";
import { MenuItemStyle } from "../components/menu-item-style";
import { resolveSourceColumn } from "./source-column";
import { asBasePrimitiveValue, isLoadedDefined } from "../components/component-helpers";
import { memoizeFunction } from "../memoize";
import { TextBoxJustify } from "./components/text-box-types";
import { Mood } from "./components/mood";
import { SeparatorSpacing } from "./components/separator-spacing";
import { ComponentIndexes } from "../component-indexes";

export const screenErrorMissingTable = "The table for this screen doesn't exist anymore.";
export const screenErrorInternal = "This screen is in an inconsistent state.";

export interface PivotContent {
    readonly componentName: string;
    readonly component: SerializedComponent;
    readonly menuItems: ReadonlyArray<SerializedMenuItem>;
}

export interface ScreenWithMenuItemsContent {
    // These are indexed by component name
    readonly pivotComponents: ReadonlyArray<PivotContent>;
    readonly additionalComponents: ReadonlyMap<string, SerializedComponent> | undefined;
    readonly ctxModifier: SerializedComponent | undefined;
    readonly title: SerializedComponent | undefined;
    readonly topBar: SerializedComponent | undefined;
    readonly pivotPicker: SerializedComponent | undefined;
    readonly quotaBannerMessage: SerializedComponent;
    readonly flags: ReadonlyArray<ScreenFlag>;
}

export function summaryForPrimitiveType(
    b: SerializedElementBuilder,
    kind: PrimitiveGlideTypeKind,
    ctx: SerializedComponent
): Summary {
    switch (kind) {
        case "boolean":
            // FIXME: Why is the subtitle the empty string, vs `undefined`?
            return [b.if(ctx, b.localizedString("yes"), b.localizedString("no")), b.constant(""), undefined, undefined];

        case "uri":
            return [ctx, undefined, undefined, ctx];

        case "image-uri":
            return [b.localizedString("image"), undefined, ctx, undefined];

        case "audio-uri":
            return [b.localizedString("audio"), undefined, ctx, undefined];

        default:
            return [ctx, undefined, undefined, undefined];
    }
}

export function makeImageSource(
    b: SerializedElementBuilder,
    data: SerializedComponent,
    imageKindDesc: LegacyPropertyDescription | undefined,
    dataIsArray: boolean
): SerializedComponent | undefined {
    const kind = withDefault(getEnumProperty<ImageKind>(imageKindDesc), ImageKind.URL);
    switch (kind) {
        case ImageKind.URL: {
            const sourceFunction = dataIsArray ? "URLArrayImageSource" : "URLImageSource";
            return b.builtinCall(sourceFunction, [data]);
        }

        case ImageKind.MapFromAddress: {
            return b.builtinCall("MapFromAddressImageSource", [data]);
        }

        default:
            return assertNever(kind);
    }
}

export function makeImageFromImageDescription(
    builder: ComponentBuilder,
    table: TableGlideType,
    desc: Partial<ImageDescription>,
    ctx: SerializedComponent
): SerializedComponent | undefined {
    if (desc.imageURLProperty === undefined) return undefined;

    const { b } = builder;

    const imageURLProperty = getColumnProperty(desc.imageURLProperty);
    if (imageURLProperty === undefined) {
        builder.error("URL property for image is not a column");
        return undefined;
    }

    const column = definedMap(imageURLProperty, p => getTableColumn(table, p));
    if (column === undefined) {
        builder.error(`Column not found in table ${getTableName(table).name} for URL image`);
        return undefined;
    }

    return makeImageSource(
        b,
        b.getProperty(imageURLProperty, ctx, table),
        desc.imageKind,
        column.type.kind === "array"
    );
}

export function summaryForTableRef(
    builder: ComponentBuilder,
    table: TableGlideType,
    desc: TitleDescription,
    ctx: SerializedComponent
): Summary {
    return [
        definedMap(getColumnProperty(desc.titleProperty), p => getPropertyWithColumnFormat(builder, table, p)),
        definedMap(getColumnProperty(desc.subtitleProperty), p => getPropertyWithColumnFormat(builder, table, p)),
        definedMap(table, t => makeImageFromImageDescription(builder, t, desc, ctx)),
        definedMap(getColumnProperty(desc.linkURLProperty), p => builder.b.getProperty(p, ctx, table)),
    ];
}

export interface OutputColumnBindingInfo {
    readonly first: ComponentDescription | ColumnAssignment;
    readonly firstName: string;
    readonly others: number;
}

export type OutputColumnBindingInfos = ReadonlyMap<string, OutputColumnBindingInfo>;

export function getEditedColumnBindings(
    desc: ClassScreenDescription,
    tables: InputOutputTables,
    ccc: ComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind | undefined,
    withActions: boolean
): OutputColumnBindingInfos {
    const properties = new Map<string, OutputColumnBindingInfo>();
    for (const component of desc.components) {
        const handler = handlerForComponentKind(component.kind);
        if (handler === undefined) {
            logError("No handler for component", component);
            continue;
        }

        const editedColumns = handler.getEditedColumns(component, tables, ccc, mutatingScreenKind, withActions);
        for (const [column, editsInScreenContext] of editedColumns) {
            if (!editsInScreenContext) continue;
            const entry = properties.get(column);
            if (entry === undefined) {
                properties.set(column, {
                    first: component,
                    firstName: handler.getDescriptiveName(component, tables, ccc)[0],
                    others: 0,
                });
            } else if (entry.first !== component) {
                // `getEditedColumns` can return duplicates, so we make sure
                // that we only count one primary one.  The count for `others`
                // might be incorrect, but we're not using it anyway, apart
                // from checking whether it's more than zero.
                properties.set(column, { ...entry, others: entry.others + 1 });
            }
        }
    }
    return properties;
}

export function getAllColumnBindings(
    desc: ClassScreenDescription,
    isEditScreen: boolean,
    tables: InputOutputTables,
    ccc: ComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind | undefined,
    withActions: boolean
): OutputColumnBindingInfos {
    const infos = new Map(getEditedColumnBindings(desc, tables, ccc, mutatingScreenKind, withActions));

    if (isEditScreen) {
        const assignments = getColumnAssignments(desc as EditScreenDescription);
        for (const assignment of assignments) {
            const { destColumn, value } = assignment;
            const entry = infos.get(destColumn);

            if (entry === undefined) {
                const handler = handlerForPropertyKind(value.kind);
                infos.set(destColumn, {
                    first: assignment,
                    firstName: handler.getColumnAssignmentName(value),
                    others: 0,
                });
            } else {
                infos.set(destColumn, { ...entry, others: entry.others + 1 });
            }
        }
    }

    return infos;
}

export function assignedColumnsInEditScreen(
    formDesc: EditScreenDescription,
    ccc: ComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind,
    withActions: boolean
): ReadonlySet<TableColumn> | undefined {
    const inputOutputTables = inputOutputTablesForClassOrArrayScreen(formDesc, ccc.schema);
    if (inputOutputTables === undefined) return undefined;

    const columns = new Set<TableColumn>();

    for (const columnName of getAllColumnBindings(
        formDesc,
        true,
        inputOutputTables,
        ccc,
        mutatingScreenKind,
        withActions
    ).keys()) {
        const column = getTableColumn(inputOutputTables.output, columnName);
        if (column === undefined) continue;

        columns.add(column);
    }

    return columns;
}

// assigned via components, actions, or column assignments
export function getColumnNamesAssignedInEditScreen(
    formDesc: EditScreenDescription,
    ccc: ComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind
): readonly string[] | undefined {
    const allAssignedColumns = assignedColumnsInEditScreen(formDesc, ccc, mutatingScreenKind, true);
    return definedMap(allAssignedColumns, a => Array.from(a).map(c => c.name));
}

// edited via components or actions
export function getColumnNamesEditedInScreen(
    desc: ClassScreenDescription,
    tables: InputOutputTables,
    ccc: ComponentConfiguratorContext,
    mutatingScreenKind: MutatingScreenKind | undefined
): ReadonlySet<string> {
    return new Set(getEditedColumnBindings(desc, tables, ccc, mutatingScreenKind, true).keys());
}

export function defaultValuesForEditScreen(
    builder: ComponentBuilder,
    desc: EditScreenDescription,
    tables: InputOutputTables
): readonly [string, SerializedComponent][] {
    const results: [string, SerializedComponent][] = [];
    for (const c of desc.components) {
        const handler = handlerForComponentKind(c.kind);
        if (handler === undefined) continue;

        results.push(...handler.getDefaultValues(builder, c, tables));
    }
    return results;
}

export function makeValueForSourceColumn(
    builder: FetchingBuilder,
    sc: SourceColumn,
    defaultContext: SerializedComponent | undefined,
    defaultTable: TableGlideType | undefined,
    containingScreenContext: SerializedComponent | undefined,
    containingScreenTable: TableGlideType | undefined,
    withFormat: boolean
): ValueForColumn | undefined {
    const { b } = builder;

    const resolved = resolveSourceColumn(builder, sc, defaultTable, containingScreenTable);
    if (resolved === undefined) return undefined;

    let ctx: SerializedComponent | undefined;
    if (sc.kind === SourceColumnKind.DefaultContext) {
        ctx = defaultContext;
    } else if (sc.kind === SourceColumnKind.UserProfile) {
        ctx = builder.makeUserProfileRow()?.row;
    } else if (sc.kind === SourceColumnKind.ContainingScreen) {
        ctx = containingScreenContext;
    } else {
        return assertNever(sc.kind);
    }

    if (ctx === undefined) return undefined;

    let tac: TableAndColumn | undefined;
    for (let i = 0; i < resolved.length; i++) {
        tac = resolved[i];
        ctx = b.getProperty(tac.column.name, ctx, tac.table);
    }
    assert(tac !== undefined);

    if (withFormat) {
        ctx = withColumnFormat(builder, ctx, tac.table, tac.column);
    }

    return { value: ctx, table: tac.table, column: tac.column };
}

export function makeFilteredArray(
    builder: FetchingBuilder,
    filter: ArrayFilter,
    rows: SerializedComponent,
    table: TableGlideType
): SerializedComponent {
    const { b } = builder;

    if (filter.operator !== BinaryPredicateFormulaOperator.Equals) {
        return assertNever(filter.operator);
    }

    function makeGetColumn(sourceColumn: SourceColumn): SerializedComponent | undefined {
        assert(sourceColumn.kind === SourceColumnKind.DefaultContext);
        return makeValueForSourceColumn(builder, sourceColumn, b.getContext, table, undefined, undefined, false)?.value;
    }

    const keyVar = b.getVariable("keyValue");
    const value = handlerForPropertyKind(filter.value.kind).makeValueForPropertyDescription(
        b,
        filter.value,
        false,
        undefined,
        makeGetColumn
    );

    if (value === undefined) {
        logError("Could not get value column for filter", filter);
        return rows;
    }

    let predicate: SerializedComponent;
    if (isEmailArrayFilter(filter)) {
        predicate = b.builtinCall("MatchesEmailFilter", [keyVar, value]);
    } else {
        predicate = b.areEqual(keyVar, value);
    }

    const filtered = b.filter(rows, predicate);
    const key = handlerForPropertyKind(filter.key.kind).makeValueForPropertyDescription(
        b,
        filter.key,
        false,
        undefined,
        makeGetColumn
    );
    if (key === undefined) {
        logError("Could not get key column for filter", filter);
        return filtered;
    }

    return b.let([["keyValue", key]], filtered);
}

export function convertForSort(
    b: SerializedElementBuilder,
    type: ColumnType,
    value: SerializedComponent
): SerializedComponent {
    switch (type.kind) {
        case "number":
            return b.convertToNumber(value);

        case "boolean":
            return b.convertToBoolean(value);

        case "date":
        case "date-time":
            return b.let([["v", value]], b.withDefault(b.parseUserDateTime(b.getVariable("v")), b.getVariable("v")));

        default:
            if (isStringTypeKind(type.kind)) {
                return b.convertToString(value);
            }
            return value;
    }
}

export function transformArray(
    builder: FetchingBuilder,
    table: TableGlideType,
    rows: SerializedComponent,
    containingScreenTable: TableGlideType | undefined,
    containingScreenContext: SerializedComponent | undefined,
    filter: ArrayFilter | undefined,
    reverse: boolean,
    transforms: readonly ArrayTransform[],
    uniqueID: string
): SerializedComponent {
    const { b } = builder;

    if (filter !== undefined) {
        rows = makeFilteredArray(builder, filter, rows, table);
    }
    if (!transforms.some(t => t.kind === ArrayTransformKind.Sort) && reverse) {
        // It shouldn't be the case that we have both a sort transform, as
        // well as `reverse` set, or at least if we have a sort then we can
        // ignore `reverse`.
        rows = b.reverseList(rows);
    }

    let limit: number | undefined;
    for (const t of transforms) {
        switch (t.kind) {
            case ArrayTransformKind.Filter: {
                if (t.isActive === false) continue;

                // We sometimes end up with this, and it's easy to short-circuit.
                if (t.predicate.kind === FormulaKind.Constant && t.predicate.value === true) {
                    break;
                }

                const compiled = compileFilter(
                    builder,
                    table,
                    t.predicate,
                    containingScreenTable,
                    containingScreenContext
                );
                if (!compiled.success) {
                    builder.error("Could not compile filter predicate", compiled);
                    continue;
                }
                rows = b.filter(rows, compiled.value);
                break;
            }

            case ArrayTransformKind.Sort: {
                if (t.keys.length === 0) continue;
                if (t.keys.length !== 1) {
                    return panic("We don't support multi-key sorts yet");
                }

                const { key, order } = t.keys[0];
                const keyCompileResult = compileFormula(
                    builder,
                    table,
                    key,
                    false,
                    false,
                    "transformArray shouldn't need unique IDs"
                );
                if (!keyCompileResult.success) {
                    builder.error("Could not compile sort key", keyCompileResult);
                    continue;
                }
                let compiledKey = keyCompileResult.value;
                if (key.kind === FormulaKind.GetColumn) {
                    const column = getTableColumn(table, key.column);
                    if (column !== undefined) {
                        compiledKey = convertForSort(b, column.type, compiledKey);
                    }
                }
                rows = b.sort(rows, compiledKey, b.constant(order));
                break;
            }

            case ArrayTransformKind.Shuffle:
                rows = b.shuffle(rows, uniqueID);
                break;

            case ArrayTransformKind.TableOrder:
                if (t.reverse) {
                    rows = b.reverseList(rows);
                }
                break;

            case ArrayTransformKind.Limit:
                limit = getNumberProperty(t.numRows);
                break;

            default:
                return assertNever(t);
        }
    }

    if (limit !== undefined) {
        rows = b.listTake(rows, b.constant(limit));
    }

    return rows;
}

interface SearchVariables {
    readonly rawSearchVariable: SerializedComponent;
    readonly trimmedSearchVariable: SerializedComponent;
}

export interface SearchBarData extends SearchVariables {
    readonly topBar: SerializedComponent;
    readonly searchLets: readonly LetBinding[];
}

export const rawSearchVariableName = "rawSearch";
export const trimmedSearchVariableName = "trimmedSearch";
export const rawSearchContextPropertyName = "search";

export function makeSearchVariables(b: SerializedElementBuilder): SearchVariables {
    return {
        rawSearchVariable: b.getVariable(rawSearchVariableName),
        trimmedSearchVariable: b.getVariable(trimmedSearchVariableName),
    };
}

export function makeSearchBar(
    b: SerializedElementBuilder,
    searchActiveFlag: SerializedComponent | undefined,
    needSeachBar: SerializedComponent | undefined,
    searchPlaceholder: SerializedComponent | undefined,
    useVariable: boolean
): SearchBarData {
    const searchVariable = b.getVariable(rawSearchVariableName);
    const placeholderComponent = withDefault(searchPlaceholder, b.localizedString("search"));
    let topBar = b.searchBar(
        useVariable ? searchVariable : b.getNonTableProperty(rawSearchContextPropertyName, b.getContext),
        placeholderComponent,
        searchActiveFlag
    );
    if (needSeachBar !== undefined) {
        topBar = b.if(needSeachBar, topBar, b.emptyUI);
    }
    return {
        topBar,
        searchLets: [
            [rawSearchVariableName, b.clonePrimitive(b.constant(""))],
            [trimmedSearchVariableName, b.builtinCall("TrimString", [searchVariable])],
        ],
        ...makeSearchVariables(b),
    };
}

export function searchableComponentsWithHandlers(
    desc: ClassScreenDescription
): readonly { handler: ComponentHandler<ComponentDescription>; description: ComponentDescription }[] {
    return mapFilterUndefined(desc.components, c => {
        const handler = handlerForComponentKind(c.kind);
        if (handler === undefined) return undefined;
        if (!handler.isSearchable(c)) return undefined;
        return { handler, description: c };
    });
}

export function searchConditionsForClassScreen(
    componentBuilder: ComponentBuilder,
    desc: ClassScreenDescription,
    tables: InputOutputTables
): readonly SerializedComponent[] {
    return mapFilterUndefined(searchableComponentsWithHandlers(desc), ({ handler, description }) =>
        handler.makeNeedsSearch(componentBuilder, description, tables)
    );
}

// We're not memoizing the `ccc` here because it really shouldn't have any
// bearing.
const getSearchableColumnsUsedInComponent = memoizeFunction(
    "getSearchableColumnsUsedInComponent",
    (cd: ComponentDescription, tables: InputOutputTables, ccc: ComponentConfiguratorContext) => {
        const handler = handlerForComponentKind(cd.kind);
        if (handler === undefined) return undefined;
        return handler.getSearchableColumnsUsed(cd, tables, ccc);
    },
    2
);

export function makeSearchedArray(
    b: SerializedElementBuilder,
    builder: ComponentBuilder,
    array: SerializedComponent,
    searchProperties: readonly string[],
    columnType: TableRefGlideType,
    searchVariable: SerializedComponent
): SerializedComponent | undefined {
    const screenDesc = builder.classScreenDescriptionForTable(columnType);
    if (screenDesc !== undefined) {
        const tables = inputOutputTablesForClassOrArrayScreen(screenDesc, builder.schema);
        if (tables !== undefined) {
            for (const cd of screenDesc.components) {
                const columns = getSearchableColumnsUsedInComponent(cd, tables, builder);
                if (columns === undefined) continue;
                searchProperties = searchProperties.concat(columns.map(c => c.name));
            }
        }
    }

    if (searchProperties.length === 0) {
        return undefined;
    }

    return b.let(
        [
            ["arr", array],
            ["ss", searchVariable],
            ["properties", b.constant(Array.from(new Set(searchProperties)))],
        ],
        b.if(
            b.stringEmpty(b.getVariable("ss")),
            b.getVariable("arr"),
            b.filter(
                b.getVariable("arr"),
                b.propertiesContainString(b.getContext, b.getVariable("properties"), b.getVariable("ss"))
            )
        )
    );
}

export function makeComponentID(screenName: string, indexes: ComponentIndexes): string {
    return JSON.stringify({ screenName, indexes });
}

export function defaultDetailScreenName(type: ColumnType): string | undefined {
    if (isPrimitiveType(type)) return undefined;
    return compoundTypeScreenName(type);
}

export function makeValidationFlagForProperty(
    b: SerializedElementBuilder,
    outputContext: SerializedComponent,
    propertyName: string | undefined,
    table: TableGlideType
): SerializedComponent | undefined {
    if (propertyName === undefined) return undefined;
    return b.isNotEmpty(b.getProperty(propertyName, outputContext, table));
}

export function makeQuotaBannerMessage(
    b: SerializedElementBuilder,
    reqs: QuotaBannerRequirements
): SerializedComponent {
    let message = b.constant(false);
    if (reqs.needListQuota) {
        message = b.if(b.builtinCall("IsRowQuotaReached", []), b.constant(QuotaBannerMessage.List), message);
    }
    if (reqs.needMapQuota) {
        message = b.if(b.builtinCall("IsGeolocationQuotaReached", []), b.constant(QuotaBannerMessage.Map), message);
    }
    return message;
}

export function makeUniqueIDForArrayScreen(screenName: string): string {
    return screenName;
}

export function makeRowIndexOrRowID(
    b: SerializedElementBuilder,
    table: TableGlideType,
    primaryKeyPropertyName: string | undefined,
    row: SerializedComponent
): RowIndexOrRowID<SerializedComponent> {
    if (table?.rowIDColumn !== undefined) {
        primaryKeyPropertyName = table.rowIDColumn;
    }
    // FIXME: This only works for sheet tables.  We should check whether
    // the table actually has a row index.
    const rowIndex = b.getProperty(rowIndexColumnName, row, table);
    if (primaryKeyPropertyName === undefined) {
        return { rowIndex };
    } else {
        return {
            keyColumnName: b.constant(primaryKeyPropertyName),
            keyColumnValue: b.getProperty(primaryKeyPropertyName, row, table),
            rowIndex,
        };
    }
}

export function makeLetsForRowIndexOrRowID(
    b: SerializedElementBuilder,
    rowIndexOrRowID: RowIndexOrRowID<SerializedComponent>
): { lets: readonly LetBinding[]; rowIndexOrRowID: RowIndexOrRowID<SerializedComponent> } {
    if (hasOwnProperty(rowIndexOrRowID, "keyColumnName")) {
        const lets: LetBinding[] = [
            ["keyColumnName", rowIndexOrRowID.keyColumnName],
            ["keyColumnValue", rowIndexOrRowID.keyColumnValue],
        ];
        let withVariables: RowIndexOrRowID<SerializedComponent> = {
            keyColumnName: b.getVariable("keyColumnName"),
            keyColumnValue: b.getVariable("keyColumnValue"),
        };
        if (rowIndexOrRowID.rowIndex !== undefined) {
            lets.push(["rowIndex", rowIndexOrRowID.rowIndex]);
            withVariables = { ...withVariables, rowIndex: b.getVariable("rowIndex") };
        }
        return {
            lets,
            rowIndexOrRowID: withVariables,
        };
    } else {
        return {
            lets: [["rowIndex", rowIndexOrRowID.rowIndex]],
            rowIndexOrRowID: {
                rowIndex: b.getVariable("rowIndex"),
            },
        };
    }
}

interface SetColumnsOptions {
    // What's the guidance on using debounce vs not?  Once we have
    // client consistency, we only need it to batch actions.  Why
    // wouldn't we always debounce?
    debounce: boolean; // default: true
    // We need the diode to buffer between modifying a value
    // directly, which is what the edit components do, and updating
    // the value in the computation model, which also entails
    // writing it to the backend.  If we didn't have the buffer,
    // updating the value might actually change the value producer,
    // due to visibility for instance, which would make the Set
    // action fail or set the wrong row.
    // What should happen ideally is that writing to the row would
    // do the backend writing all by itself.  We could do that if
    // we had a special value producer for table rows, which also
    // knows what it's supposed to index by, and which table it
    // belongs to.
    withDiode: boolean | string; // default: true
}

const defaultSetColumnsOptions: SetColumnsOptions = {
    debounce: true,
    withDiode: true,
};

export interface SetColumnData {
    readonly propertyValue: SerializedComponent;
    readonly defaultValue: SerializedComponent | undefined;
}

export function setColumnsInRowForEditComponent(
    b: SerializedElementBuilder,
    mutatingScreenKind: MutatingScreenKind | undefined,
    columnData: Record<string, SetColumnData>, // key is column name
    table: TableGlideType,
    // This will be used unless we have a row ID
    primaryKeyPropertyName: string | undefined,
    existingRow: SerializedComponent,
    options?: Partial<SetColumnsOptions>
):
    | {
          lets: readonly LetBinding[];
          valuesToModify: Record<string, SerializedComponent>;
          action: SerializedComponent | undefined;
          isEnabled: SerializedComponent | undefined;
      }
    | undefined {
    const { debounce, withDiode } = { ...defaultSetColumnsOptions, ...options };
    const tableName = getTableName(table);
    const rowIndexOrRowID = makeRowIndexOrRowID(b, table, primaryKeyPropertyName, existingRow);

    const lets: LetBinding[] = [];
    const actions: SerializedComponent[] = [];
    const valuesToModify: Record<string, SerializedComponent> = {};

    for (const [propertyName, { propertyValue, defaultValue }] of entries(columnData)) {
        const column = getTableColumn(table, propertyName);
        if (column === undefined || isComputedColumn(column)) return undefined;

        let valueToModify: SerializedComponent;
        if (withDiode !== false && mutatingScreenKind === undefined) {
            const diodeName = withDiode === true ? `diode-${propertyName}` : withDiode;
            lets.push([diodeName, b.primitiveDiode(propertyValue, defaultValue)]);
            valueToModify = b.getVariable(diodeName);
        } else {
            valueToModify = propertyValue;
        }

        valuesToModify[propertyName] = valueToModify;
    }

    if (mutatingScreenKind !== undefined) {
        assert(actions.length === 0);
        return { lets, valuesToModify, action: undefined, isEnabled: undefined };
    } else {
        const { action, isEnabled } = b.setColumnsInRowForColumnValues(
            valuesToModify,
            tableName.name,
            rowIndexOrRowID,
            debounce
        );
        return { lets, valuesToModify, action, isEnabled };
    }
}

export function setColumnInRowForEditComponent(
    b: SerializedElementBuilder,
    mutatingScreenKind: MutatingScreenKind | undefined,
    propertyName: string,
    propertyValue: SerializedComponent,
    defaultValue: SerializedComponent | undefined,
    table: TableGlideType,
    // This will be used unless we have a row ID
    primaryKeyPropertyName: string | undefined,
    existingRow: SerializedComponent,
    options?: Partial<SetColumnsOptions>
):
    | {
          lets: readonly LetBinding[];
          valueToModify: SerializedComponent;
          action: SerializedComponent | undefined;
          isEnabled: SerializedComponent | undefined;
      }
    | undefined {
    const columnData: Record<string, SetColumnData> = {
        [propertyName]: { propertyValue, defaultValue },
    };
    const result = setColumnsInRowForEditComponent(
        b,
        mutatingScreenKind,
        columnData,
        table,
        primaryKeyPropertyName,
        existingRow,
        options
    );
    if (result === undefined) return undefined;
    const { lets, valuesToModify, action, isEnabled } = result;

    return { lets, valueToModify: defined(valuesToModify[propertyName]), action, isEnabled };
}

export function makeFavoriteAction(
    b: SerializedElementBuilder,
    table: TableGlideType,
    mutatingScreenKind: MutatingScreenKind | undefined,
    primaryKeyColumn: string | undefined
):
    | {
          lets: readonly LetBinding[];
          value: SerializedComponent;
          onChange: SerializedComponent;
          isEnabled: SerializedComponent | undefined;
      }
    | undefined {
    if (getTableColumn(table, isFavoritedColumnName) === undefined) return undefined;

    const setColumnsResult = setColumnInRowForEditComponent(
        b,
        mutatingScreenKind,
        isFavoritedColumnName,
        b.getVariable("toSave"),
        undefined,
        table,
        primaryKeyColumn,
        b.getContext,
        // FIXME: Technically we need the diode here, too, but I'm not sure
        // about the interaction between this diode and the one we're building
        // below.  I suspect we can get rid of one below completely if we use
        // this one properly.
        { debounce: false, withDiode: false }
    );
    if (setColumnsResult === undefined) return undefined;
    const { action, isEnabled } = setColumnsResult;
    if (action === undefined) return undefined;

    // FIXME: only do this complex thing if the app doesn't have email authentication

    const lets: LetBinding[] = [
        ["original", b.convertToBoolean(b.getProperty(isFavoritedColumnName, b.getContext, table))],
        ["copy", b.primitiveDiode(b.getVariable("original"), b.constant(false))],
        ["toSave", b.data(false)],
        ["setColumn", b.bindContextToValue(action, b.getContext)],
    ];
    const value = b.getVariable("copy");

    const onChange = b.sequence([
        b.set(b.getVariable("toSave"), value),
        b.actionWithUserSpecificData(b.getVariable("setColumn"), b.sequence([b.set(value, b.getVariable("original"))])),
    ]);

    return {
        lets,
        value,
        onChange,
        isEnabled,
    };
}

export function iconForActions(actions: readonly ActionDescription[]): Icon | true | undefined {
    if (actions.length === 0) return undefined;
    return handlerForActionKind(actions[0].kind)?.iconName;
}

export enum LinkAppearance {
    PageTitle = "page-title",
    LastPathComponent = "last-path-component",
    ShortURL = "short-url",
    FullURL = "full-url",
    CaptionAsTitle = "caption-as-title",
}

export function makePushWebViewScreen(
    b: SerializedElementBuilder,
    link: SerializedComponent,
    appearance: LinkAppearance
): SerializedComponent {
    let title: SerializedComponent;
    switch (appearance) {
        case LinkAppearance.ShortURL:
            title = b.shortenLink(b.getVariable("link"));
            break;
        case LinkAppearance.FullURL:
            // FIXME: column format?
            title = b.getVariable("link");
            break;
        case LinkAppearance.LastPathComponent:
            title = b.getLastURLPathComponent(b.getVariable("link"));
            break;
        // We don't implement `CaptionAsTitle`
        case LinkAppearance.CaptionAsTitle:
        case LinkAppearance.PageTitle:
            title = b.getNonTableProperty("title", b.classifyLink(b.getVariable("link")));
            break;
        default:
            return assertNever(appearance);
    }

    return b.actionIfContextDefined(
        b.let(
            [
                ["link", link],
                ["title", title],
            ],
            b.pushModalBuilderScreen(
                webViewScreenName,
                b.object({
                    link: b.getVariable("link"),
                    title: b.getVariable("title"),
                }),
                b.getVariable("title"),
                true,
                true
            )
        )
    );
}

export function handleActionIcon(
    b: SerializedElementBuilder,
    onTap: SerializedComponent | undefined,
    icon: SerializedComponent | undefined,
    withHoldContainer: boolean,
    makeItem: (onTap: SerializedComponent | undefined, icon: SerializedComponent | undefined) => SerializedComponent
): SerializedComponent {
    if (onTap === undefined || icon === undefined) {
        return makeItem(onTap, icon);
    }
    let ui = b.let([["onTap", onTap]], makeItem(b.getVariable("onTap"), b.ifIsDefined(b.getVariable("onTap"), icon)));
    if (withHoldContainer) {
        ui = b.holdContainer(ui);
    }
    return ui;
}

export function makePostCommentAction(
    builder: ComponentBuilder,
    topic: SerializedComponent,
    title: LocalizedStringKey,
    placeholder: LocalizedStringKey,
    sendText: LocalizedStringKey,
    sendStyle: MenuItemStyle
): SerializedComponent {
    const { b } = builder;

    return b.holdAction(
        b.let(
            [["comment", b.clonePrimitive(b.constant(""))]],
            builder.makePushOverlayEditorScreen(
                b.getVariable("comment"),
                b.localizedString(title),
                b.localizedString(placeholder),
                b.localizedString(sendText),
                b.constant("send"),
                b.constant(sendStyle),
                false,
                b.bindContextToValue(
                    b.postComment(topic),
                    b.object({
                        comment: b.getVariable("comment"),
                        email: b.verifiedEmailAddress(),
                        // FIXME: Use the proper username if we have user profiles
                        username: b.fallbackUsername(),
                    })
                )
            )
        )
    );
}

export function makeTextWithTypeKind(
    builder: ComponentBuilder,
    desc: LegacyPropertyDescription | undefined,
    table: TableGlideType,
    // Don't return a serialized component if the property is an empty
    // string.
    undefinedIfEmpty: boolean = false,
    // Will apply the column format if there is one.
    withFormat: boolean = true,
    // This is legacy stuff.
    columnFirst: boolean = false,
    // Will this value be written to?
    mutable: boolean = false,
    context?: SerializedComponent
): { text: SerializedComponent; typeKind: ColumnTypeKind | undefined } | undefined {
    const sourceColumnData = definedMap(getSourceColumnProperty(desc), c =>
        makeValueForSourceColumn(builder, c, context ?? builder.b.getContext, table, undefined, undefined, withFormat)
    );
    let sourceColumn = sourceColumnData?.value;
    if (sourceColumn !== undefined && mutable) {
        sourceColumn = builder.b.clonePrimitive(sourceColumn);
    }
    if (sourceColumn !== undefined && columnFirst) {
        return { text: sourceColumn, typeKind: sourceColumnData?.column?.type.kind };
    }

    let stringProperty = getStringProperty(desc as LegacyPropertyDescription);
    if (stringProperty !== undefined) {
        if (undefinedIfEmpty) {
            stringProperty = undefinedIfEmptyString(stringProperty);
        }
        if (stringProperty === undefined) return undefined;
        return {
            text: mutable ? builder.b.data(stringProperty) : builder.b.constant(stringProperty),
            // FIXME: Try parsing with all heuristics we know.  There are a bunch
            // of the in the sheet mirror.
            typeKind: isUrl(stringProperty) ? "uri" : undefined,
        };
    }

    if (sourceColumn !== undefined) {
        return { text: sourceColumn, typeKind: sourceColumnData?.column?.type.kind };
    }

    const specialValue = getSpecialValueProperty(desc);
    if (specialValue !== undefined && isPrimitiveTypeKind(specialValueTypeKinds[specialValue])) {
        return { text: builder.b.specialValue(specialValue), typeKind: specialValueTypeKinds[specialValue] };
    }

    return undefined;
}

export function makeText(
    builder: ComponentBuilder,
    desc: LegacyPropertyDescription | undefined,
    table: TableGlideType,
    // Don't return a serialized component if the property is an empty
    // string.
    undefinedIfEmpty: boolean = false,
    // Will apply the column format if there is one.
    withFormat: boolean = true,
    // This is legacy stuff.
    columnFirst: boolean = false,
    // Will this value be written to?
    mutable: boolean = false,
    context?: SerializedComponent
): SerializedComponent | undefined {
    return makeTextWithTypeKind(builder, desc, table, undefinedIfEmpty, withFormat, columnFirst, mutable, context)
        ?.text;
}

export function makeNumber(
    builder: ComponentBuilder,
    desc: PropertyDescription,
    table: TableGlideType,
    mutable: boolean = false,
    context?: SerializedComponent
): SerializedComponent | undefined {
    let sourceColumn = definedMap(
        getSourceColumnProperty(desc),
        c =>
            makeValueForSourceColumn(builder, c, context ?? builder.b.getContext, table, undefined, undefined, false)
                ?.value
    );
    if (sourceColumn !== undefined && mutable) {
        sourceColumn = builder.b.clonePrimitive(sourceColumn);
    }
    if (sourceColumn !== undefined) {
        return sourceColumn;
    }

    const numberProperty = getNumberProperty(desc as PropertyDescription);
    return definedMap(numberProperty, mutable ? builder.b.data : builder.b.constant);
}

export function withCaption(
    builder: ComponentBuilder,
    table: TableGlideType,
    desc: LegacyPropertyDescription | undefined,
    lets: readonly LetBinding[],
    f: (caption: SerializedComponent | undefined) => SerializedComponent
): SerializedComponent {
    let caption = makeText(builder, desc, table, true);
    if (caption !== undefined) {
        lets = [...lets, ["caption", caption]];
        caption = builder.b.getVariable("caption");
    }
    return builder.b.let(lets, f(caption));
}

// This is closely tied to `makeDefaultValuePropertyDescriptor`
export function getDefaultValuesForComponent(
    builder: ComponentBuilder,
    desc: DefaultableComponentDescription | [PropertyDescription, PropertyDescription | undefined],
    table: TableGlideType,
    fieldType: ColumnTypeKind | undefined
): readonly [string, SerializedComponent][] {
    const { b } = builder;

    if (fieldType === undefined) return [];

    let propertyNameDesc: PropertyDescription;
    let defaultValueDesc: PropertyDescription | undefined;
    if (isArray(desc)) {
        propertyNameDesc = desc[0];
        defaultValueDesc = desc[1];
    } else {
        propertyNameDesc = desc.propertyName;
        defaultValueDesc = desc.defaultValue;
    }

    let defaultValue: SerializedComponent | undefined;
    if (isNumberTypeKind(fieldType)) {
        defaultValue = definedMap(defaultValueDesc, d => makeNumber(builder, d, table));
    } else if (fieldType === "boolean") {
        const boolean = getEnumProperty(defaultValueDesc);
        if (typeof boolean === "boolean") {
            defaultValue = b.constant(boolean);
        }
    } else if (isDateTimeTypeKind(fieldType)) {
        const dateString = getStringProperty(defaultValueDesc);
        if (dateString !== undefined) {
            // This is ugly and stupid.  The only reason we're calling this here is
            // to asynchronously load the date parsing code, so that maybe we'll be
            // able to parse it in the next line, or next time we run this code.
            const maybePromise = parseUserDateTimeZoneAgnostic(dateString);
            if (maybePromise instanceof Promise) {
                dontAwait(maybePromise);
            }

            const parsed = parseUserDateTimeZoneAgnosticSync(dateString);
            if (parsed !== undefined) {
                defaultValue = b.constant(parsed.toISOString());
            }
        } else {
            const enumValue = getEnumProperty(defaultValueDesc);
            if (enumValue === "now") {
                defaultValue = b.specialValue(SpecialValueKind.Timestamp);
                if (fieldType === "date") {
                    defaultValue = b.startOfDay(defaultValue);
                }
            }
        }
    } else {
        defaultValue = makeText(builder, defaultValueDesc, table, true, false, false);
        if (defaultValue === undefined) {
            // This is only used in Choice
            const enumValue = getEnumProperty(defaultValueDesc);
            if (isLoadedDefined(enumValue)) {
                defaultValue = b.constant(asBasePrimitiveValue(enumValue));
            }
        }
    }

    if (defaultValue === undefined) {
        defaultValue = definedMap(
            getSourceColumnProperty(defaultValueDesc),
            c => makeValueForSourceColumn(builder, c, b.getContext, table, undefined, undefined, false)?.value
        );
    }

    const propertyName = getColumnProperty(propertyNameDesc);

    if (defaultValue === undefined || propertyName === undefined) return [];

    return [[propertyName, b.clonePrimitive(defaultValue)]];
}

export function makeErrorScreen(
    b: SerializedElementBuilder,
    screenName: string,
    message: string
): ScreenWithMenuItemsContent {
    return {
        pivotComponents: [
            {
                componentName: screenName,
                component: b.column([
                    b.ui("Separator", { spacing: b.constant(SeparatorSpacing.Default), showLine: b.constant(false) }),
                    b.ui("Hint", {
                        title: b.constant("Error"),
                        text: b.constant(message),
                        mood: b.constant(Mood.Danger),
                        justify: b.constant(TextBoxJustify.Left),
                    }),
                ]),
                menuItems: [],
            },
        ],
        additionalComponents: undefined,
        ctxModifier: undefined,
        title: undefined,
        topBar: undefined,
        pivotPicker: undefined,
        quotaBannerMessage: b.constant(false),
        flags: [ScreenFlag.IsErrorScreen],
    };
}

export function makeSummaryForType(
    b: SerializedElementBuilder,
    column: ColumnType,
    summary: Summary | undefined
): Summary {
    switch (column.kind) {
        case "array":
            return [b.smartNumberOfItems(b.getContext, b.localizedString("noItems")), undefined, undefined, undefined];

        case "table-ref":
            if (summary === undefined) {
                return panic("Description for table-ref must be compound");
            }
            return summary;

        default:
            if (!isPrimitiveType(column)) {
                return assertNever(column);
            }
            return summaryForPrimitiveType(b, column.kind, b.getContext);
    }
}

export function compileFilters(
    builder: ComponentBuilder,
    table: TableGlideType | undefined,
    context: SerializedComponent,
    transforms: readonly ArrayTransform[],
    condition: SerializedComponent
): SerializedComponent;
export function compileFilters(
    builder: ComponentBuilder,
    table: TableGlideType | undefined,
    context: SerializedComponent,
    transforms: readonly ArrayTransform[],
    condition?: SerializedComponent
): SerializedComponent | undefined;
export function compileFilters(
    builder: ComponentBuilder,
    table: TableGlideType | undefined,
    context: SerializedComponent,
    transforms: readonly ArrayTransform[],
    condition?: SerializedComponent
): SerializedComponent | undefined {
    const { b } = builder;

    for (const f of withDefault(transforms, [])) {
        if (f.kind !== ArrayTransformKind.Filter) {
            return panic(`Component visibility must be filter, but is ${f.kind}`);
        }

        if (f.isActive === false) continue;

        const compiled = compileFormula(
            builder,
            table,
            f.predicate,
            false,
            false,
            "compileFilters shouldn't need unique IDs"
        );
        if (!compiled.success) {
            // logError("Could not compile visibility predicate", compiled);
            continue;
        }

        let { value } = compiled;
        if (context !== b.getContext) {
            value = b.bindContextToValue(compiled.value, context);
        }
        if (condition === undefined) {
            condition = value;
        } else {
            condition = b.and(condition, value);
        }
    }

    return condition;
}
